import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { benachrichtigungen, type PcBenachrichtigung } from '../api';

/**
 * Die Glocke in der Kopfzeile.
 *
 * Fragt nur den Zähler ab, solange sie zu ist — eine Zahl statt einer Liste,
 * jede Minute. Die Einträge kommen erst beim Öffnen. Bei fünfzig geöffneten
 * Tabs wäre der Unterschied sonst spürbar, und zwar auf dem Server.
 *
 * Ein Fehler beim Abruf bleibt still: eine Glocke, die rot blinkt, weil das
 * Netz kurz weg war, erzieht dazu, sie zu ignorieren.
 */
export default function Benachrichtigungsglocke() {
  const [offen, setOffen] = useState(false);
  const [ungelesen, setUngelesen] = useState(0);
  const [eintraege, setEintraege] = useState<PcBenachrichtigung[]>([]);
  const huelle = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const zaehlen = useCallback(async () => {
    try {
      const { ungelesen } = await benachrichtigungen.anzahl();
      setUngelesen(ungelesen);
    } catch {
      /* still */
    }
  }, []);

  useEffect(() => {
    zaehlen();
    const t = setInterval(zaehlen, 60_000);
    return () => clearInterval(t);
  }, [zaehlen]);

  useEffect(() => {
    if (!offen) return;
    (async () => {
      try {
        const d = await benachrichtigungen.liste({ limit: 30 });
        setEintraege(d.eintraege);
        setUngelesen(d.ungelesen);
      } catch {
        /* still */
      }
    })();
  }, [offen]);

  // Klick daneben schließt. Ohne das bleibt die Liste offen, während man
  // woanders weiterarbeitet, und verdeckt den halben Bildschirm.
  useEffect(() => {
    if (!offen) return;
    const zu = (e: MouseEvent) => {
      if (huelle.current && !huelle.current.contains(e.target as Node)) setOffen(false);
    };
    document.addEventListener('mousedown', zu);
    return () => document.removeEventListener('mousedown', zu);
  }, [offen]);

  async function anklicken(n: PcBenachrichtigung) {
    if (!n.gelesenAm) {
      await benachrichtigungen.gelesen(n.id).catch(() => undefined);
      setUngelesen((u) => Math.max(0, u - 1));
      setEintraege((e) =>
        e.map((x) => (x.id === n.id ? { ...x, gelesenAm: new Date().toISOString() } : x)),
      );
    }
    if (n.link) {
      setOffen(false);
      // Der Link kommt vom Server als vollständige Adresse. Innerhalb der
      // eigenen Anwendung soll er aber nicht die Seite neu laden.
      try {
        const u = new URL(n.link, window.location.origin);
        if (u.origin === window.location.origin) navigate(u.pathname + u.search);
        else window.open(n.link, '_blank', 'noopener');
      } catch {
        navigate(n.link);
      }
    }
  }

  const farbe = (s: string) =>
    s === 'critical' ? 'bg-red-600' : s === 'warning' ? 'bg-amber-500' : 'bg-teal-600';

  return (
    <div className="relative" ref={huelle}>
      <button
        onClick={() => setOffen((o) => !o)}
        className="relative rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
        aria-label={`Benachrichtigungen${ungelesen > 0 ? `, ${ungelesen} ungelesen` : ''}`}
      >
        <Bell size={18} />
        {ungelesen > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
            {ungelesen > 99 ? '99+' : ungelesen}
          </span>
        )}
      </button>

      {offen && (
        <div className="absolute right-0 z-50 mt-1 w-80 rounded-md border border-slate-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
            <span className="text-xs font-semibold text-slate-700">Benachrichtigungen</span>
            {ungelesen > 0 && (
              <button
                onClick={async () => {
                  await benachrichtigungen.alleGelesen().catch(() => undefined);
                  setUngelesen(0);
                  setEintraege((e) =>
                    e.map((x) => ({ ...x, gelesenAm: x.gelesenAm ?? new Date().toISOString() })),
                  );
                }}
                className="text-xs text-slate-500 hover:text-slate-700"
              >
                Alle gelesen
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {eintraege.length === 0 ? (
              <p className="px-3 py-4 text-xs text-slate-400">Nichts Neues.</p>
            ) : (
              eintraege.map((n) => (
                <button
                  key={n.id}
                  onClick={() => anklicken(n)}
                  className={`flex w-full gap-2 border-b border-slate-50 px-3 py-2 text-left hover:bg-slate-50 ${
                    n.gelesenAm ? 'opacity-60' : ''
                  }`}
                >
                  <span
                    className={`mt-1 h-2 w-2 shrink-0 rounded-full ${farbe(n.schwere)}`}
                  />
                  <span className="min-w-0">
                    <span className="block text-xs font-medium text-slate-800">{n.titel}</span>
                    {n.text && (
                      <span className="mt-0.5 block whitespace-pre-line text-xs text-slate-500">
                        {n.text.length > 160 ? `${n.text.slice(0, 160)}…` : n.text}
                      </span>
                    )}
                    <span className="mt-0.5 block text-[10px] text-slate-400">
                      {new Date(n.createdAt).toLocaleString('de-DE')}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
